import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Languages, LayoutDashboard, User as UserIcon, LogOut, ShieldCheck } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger
} from './ui/dropdown-menu';

const LOGO_BLACK = 'https://customer-assets.emergentagent.com/job_additive-factory-3/artifacts/hikohrp1_RD_Logo_Black.png';

const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const { user, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: '/', label: t.nav.home },
    { to: '/quote', label: t.nav.quote },
    { to: '/services', label: t.nav.services },
    { to: '/about', label: t.nav.about }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${scrolled || open ? 'bg-white/95 backdrop-blur-md border-b border-zinc-200 shadow-sm' : 'bg-white/80 backdrop-blur-sm'}`}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-28 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <img src={LOGO_BLACK} alt="ROLVIA DESIGN" className="h-20 md:h-24 w-auto object-contain select-none" draggable={false} />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === '/'}
              className={({ isActive }) =>
                `px-3 py-2 text-sm rounded-md transition-colors ${isActive ? 'text-orange-500' : 'text-zinc-700 hover:text-zinc-900'}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={toggle} className="hidden sm:flex items-center gap-1.5 text-zinc-600 hover:text-orange-500 text-sm px-2 py-1.5 rounded-md" aria-label="toggle language">
            <Languages className="w-4 h-4" />
            <span className="uppercase font-medium">{lang}</span>
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden md:flex items-center gap-2 px-2 py-1.5 rounded-full hover:bg-zinc-100">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-zinc-200" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-sm">
                      {user.name?.[0]?.toUpperCase() || 'R'}
                    </div>
                  )}
                  <span className="text-sm text-zinc-700 font-medium max-w-[110px] truncate">{user.name?.split(' ')[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <DropdownMenuLabel className="text-zinc-500 text-xs truncate">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/dashboard"><LayoutDashboard className="w-4 h-4 mr-2" /> {t.auth.dashboard}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/dashboard/profile"><UserIcon className="w-4 h-4 mr-2" /> {t.auth.profile}</Link>
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin"><ShieldCheck className="w-4 h-4 mr-2 text-orange-500" /> {t.auth.admin}</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-rose-600 focus:text-rose-700">
                  <LogOut className="w-4 h-4 mr-2" /> {t.auth.logout}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button onClick={login} className="hidden md:inline-flex items-center text-sm text-zinc-700 hover:text-orange-500 transition-colors px-3 py-2">
              {t.auth.login}
            </button>
          )}

          <Link to="/quote" className="hidden md:inline-flex">
            <Button className="bg-orange-500 hover:bg-orange-600 text-white font-semibold border-0">{t.nav.getQuote}</Button>
          </Link>
          <button className="md:hidden text-zinc-900 p-2" onClick={() => setOpen(!open)} aria-label="menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-zinc-200">
          <div className="px-5 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === '/'}
                className={({ isActive }) =>
                  `py-2 px-2 rounded-md text-base ${isActive ? 'text-orange-500' : 'text-zinc-800'}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <Link to="/dashboard" className="py-2 px-2 text-zinc-800">{t.auth.dashboard}</Link>
                <Link to="/dashboard/profile" className="py-2 px-2 text-zinc-800">{t.auth.profile}</Link>
                {user.is_admin && <Link to="/admin" className="py-2 px-2 text-orange-600">{t.auth.admin}</Link>}
                <button onClick={logout} className="py-2 px-2 text-left text-rose-600">{t.auth.logout}</button>
              </>
            ) : (
              <button onClick={login} className="py-2 px-2 text-left text-zinc-800">{t.auth.login}</button>
            )}
            <button onClick={toggle} className="flex items-center gap-2 py-2 px-2 text-zinc-800 text-base">
              <Languages className="w-4 h-4" /> {lang.toUpperCase()}
            </button>
            <Link to="/quote" className="mt-2">
              <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold border-0">{t.nav.getQuote}</Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
